import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, MessageCircle, Clock, Instagram, Music2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { CONTACT, LOGO, SORTIMENT } from "@/content";
import { useSettings } from "@/hooks/useSettings";

const pageLinks = [
  { to: "/", label: "Home", testid: "footer-home-link" },
  { to: "/events", label: "Events", testid: "footer-events-link" },
  { to: "/ankauf", label: "Ankauf", testid: "footer-ankauf-link" },
  { to: "/anfahrt", label: "Anfahrt", testid: "footer-anfahrt-link" },
  { to: "/ueber-uns", label: "Über uns", testid: "footer-ueber-uns-link" },
  { to: "/kontakt", label: "Kontakt", testid: "footer-kontakt-link" },
];

const legalLinks = [
  { to: "/recht/impressum", label: "Impressum", testid: "footer-impressum-link" },
  { to: "/recht/datenschutz", label: "Datenschutzerklärung", testid: "footer-datenschutz-link" },
  { to: "/recht/widerruf", label: "Widerrufsrecht", testid: "footer-widerruf-link" },
  { to: "/recht/agb", label: "AGB", testid: "footer-agb-link" },
];

export const Footer = () => {
  const { hours } = useSettings();
  return (
    <footer data-testid="site-footer" className="border-t border-border bg-card">
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-5">
          <div className="sm:col-span-2 lg:col-span-2">
            <img src={LOGO} alt="Shibuya Anime & TCG Store" className="h-14 w-auto" />
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground">
              Dein Spot für Pokémon &amp; One Piece TCG, Anime-Figuren und echte
              Community-Vibes – mitten in Köln, im Tokyo-Style.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex items-start gap-2 text-muted-foreground">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>
                  {CONTACT.addressStreet}, {CONTACT.addressCity}
                </span>
              </li>
              <li className="flex items-start gap-2 text-muted-foreground">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span data-testid="footer-hours">
                  {hours.map((row) => (
                    <span key={row.days} className="block">
                      {row.days}: {row.time}
                    </span>
                  ))}
                </span>
              </li>
              <li>
                <a
                  href={CONTACT.phoneHref}
                  data-testid="footer-phone-link"
                  className="flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Phone className="h-4 w-4 text-primary" /> {CONTACT.phoneDisplay}
                </a>
              </li>
              <li>
                <a
                  href={CONTACT.emailHref}
                  data-testid="footer-email-link"
                  className="flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Mail className="h-4 w-4 text-primary" /> {CONTACT.email}
                </a>
              </li>
              <li>
                <a
                  href={CONTACT.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
                >
                  <MessageCircle className="h-4 w-4 text-primary" /> WhatsApp schreiben
                </a>
              </li>
            </ul>
            <div className="mt-6 flex items-center gap-3">
              <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" aria-label="Instagram" className="grid h-9 w-9 place-items-center rounded-xl border border-border text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground">
                <Instagram className="h-4 w-4" />
              </a>
              <a href={CONTACT.tiktok} target="_blank" rel="noopener noreferrer" data-testid="footer-tiktok-link" aria-label="TikTok" className="grid h-9 w-9 place-items-center rounded-xl border border-border text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground">
                <Music2 className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div>
            <p className="font-mono-ui text-xs uppercase tracking-widest text-muted-foreground">
              Sortiment
            </p>
            <ul className="mt-4 space-y-3 text-sm">
              {SORTIMENT.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-sortiment-${link.to}`}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-mono-ui text-xs uppercase tracking-widest text-muted-foreground">
              Seiten
            </p>
            <ul className="mt-4 space-y-3 text-sm">
              {pageLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={link.testid}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-mono-ui text-xs uppercase tracking-widest text-muted-foreground">
              Rechtliches
            </p>
            <ul className="mt-4 space-y-3 text-sm">
              {legalLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={link.testid}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Separator className="my-8 bg-border" />

        <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} {CONTACT.storeName}. Alle Rechte vorbehalten.
          </p>
          <p className="font-mono-ui text-[11px] uppercase tracking-widest text-muted-foreground">
            渋谷 — Tokyo Style in Köln
          </p>
        </div>
      </div>
    </footer>
  );
};
